import { Selector } from '@ngrx/store';

import { ConfigObject, MakeRef, MetaState, PaginationState, Reference } from '../types';

export type ID = string | number;
export const DEFAULT_SCOPE_NAME = 'main';
export type Scope = typeof DEFAULT_SCOPE_NAME | string;

export type GenericIdSelector<Entity extends GenericEntity> = (entity: Entity) => ID;

export type DefaultIdSelector<Entity extends GenericEntity> = Entity extends {
  id: ID;
} & GenericEntity
  ? GenericIdSelector<{ id: Entity['id'] }>
  : GenericIdSelector<Entity>;

export type IdSelectorOrDefault<
  IdSelector extends GenericIdSelector<Entity> | undefined,
  Entity extends GenericEntity,
> = IdSelector extends GenericIdSelector<Entity> ? IdSelector : DefaultIdSelector<Entity>;

export type OptimisticUpdate<Entity extends GenericEntity> = {
  update: EntityPartial<Entity>;
  before: Entity;
};

export interface EntityState<
  Entity extends GenericEntity,
  MetaOptions extends GenericMetaOptions = GenericMetaOptions,
> {
  entityMap: Record<ID, Entity | null>;
  entityMetaMap: Record<ID, MetaState>;
  scope: Record<Scope, ID[]>;
  scopeMeta: Record<Scope, MetaState<InferScopeMeta<MetaOptions>>>;
  aliases: Record<ID, ID>;
  optimisticUpdate: Record<string, OptimisticUpdate<Entity>>;
  optimisticDelete: Record<ID, Entity>;
  scopePagination: Record<Scope, PaginationState>;
}

export interface CorrelationId {
  correlationId?: string;
}

export interface EntityAdapterOptions {
  scope?: string;
}

export interface PrependingOption {
  prepend?: boolean;
}

export interface GenericEntity {
  [key: string]: any;
}

export interface GenericMetaOptions {
  scopeMeta?: object;
}

export type EntityPartial<Entity extends GenericEntity> = Partial<Entity>;

export type BaseSelector<
  Entity extends GenericEntity,
  MetaOptions extends GenericMetaOptions,
> = Selector<any, EntityState<Entity, MetaOptions>>;

export type MetaScopeOptions = { scope: Scope | null };
export type MetaEntitiesOptions = { entities: ID[] };
export type MetaActionOptions = MetaScopeOptions & MetaEntitiesOptions;

export type InferScopeMeta<T> = T extends { scopeMeta?: infer R } ? R : never;

export interface EntityMetaAdapter<
  Entity extends GenericEntity,
  MetaOptions extends GenericMetaOptions,
> {
  markAsPending: <T1 extends GenericEntity>(
    state: EntityState<T1, MetaOptions>,
    options?: MetaActionOptions,
  ) => EntityState<T1, MetaOptions>;
  markAsLoaded: <T1 extends GenericEntity>(
    state: EntityState<T1, MetaOptions>,
    options?: MetaActionOptions,
  ) => EntityState<T1, MetaOptions>;
  setError: (
    state: EntityState<Entity, MetaOptions>,
    error: any,
    options?: MetaActionOptions,
  ) => EntityState<Entity, MetaOptions>;
  setPaginationMeta: (
    state: EntityState<Entity, MetaOptions>,
    paginationMeta: PaginationState,
    options?: MetaScopeOptions,
  ) => EntityState<Entity, MetaOptions>;
  // alias correlationId -> id for remote create operations
  setAlias: (
    state: EntityState<Entity, MetaOptions>,
    data: Record<ID, ID>,
  ) => EntityState<Entity, MetaOptions>;
  setScopeMetaCustom: (
    state: EntityState<Entity, MetaOptions>,
    data: InferScopeMeta<MetaOptions>,
    options?: MetaScopeOptions,
  ) => EntityState<Entity, MetaOptions>;
}

export interface EntityDataAdapter<
  Entity extends GenericEntity,
  MetaOptions extends GenericMetaOptions,
  State extends EntityState<Entity, MetaOptions> = EntityState<Entity, MetaOptions>,
> {
  // Add to scope (uniq), set entity
  addOne: (
    state: State,
    data: Entity,
    options?: EntityAdapterOptions & CorrelationId & PrependingOption,
  ) => State;
  addMany: (
    state: State,
    data: Entity[],
    options?: EntityAdapterOptions & PrependingOption,
  ) => State;

  // Set scope, set entity
  setOne: (state: State, data: Entity, options?: EntityAdapterOptions) => State;
  setMany: (state: State, data: Entity[], options?: EntityAdapterOptions) => State;

  // Doesn't touch scope, set entity
  writeOne: (state: State, data: Entity) => State;
  writeMany: (state: State, data: Entity[]) => State;

  // Replace entity in all scopes, set entity
  replaceOne: (state: State, previousId: ID, current: Entity) => State;
  replaceMany: (state: State, data: { previousId: ID; current: Entity }[]) => State;

  // Removes entity from all scopes, removes entity
  removeOne: (state: State, id: ID) => State;
  removeMany: (state: State, ids: ID[]) => State;

  // Doesn't touch scope, updates entity (spread)
  updateOne: (state: State, data: EntityPartial<Entity>) => State;
  updateMany: (state: State, data: EntityPartial<Entity>[]) => State;

  // Doesn't touch scope, updates or creates entity (spread)
  updateOrCreateMany: (state: State, data: Entity[]) => State;

  // Add to scope if not exist, update or add entity
  upsertOne: (state: State, data: Entity, options?: EntityAdapterOptions) => State;
  upsertMany: (state: State, data: Entity[], options?: EntityAdapterOptions) => State;

  optimisticUpdateSet: (state: State, correlationId: ID, data: OptimisticUpdate<Entity>) => State;
  optimisticUpdateRemove: (state: State, correlationId: string) => State;
  optimisticDeleteSet: (state: State, data: Entity) => State;
  optimisticDeleteRemove: (state: State, id: ReturnType<GenericIdSelector<Entity>>) => State;

  // Remove all entities from scope
  resetScope: (state: State, scope?: Scope) => State;
  // Reset to initial state
  resetAll: (state: State) => State;
}

export type EntityCollectionAdapter<
  Entity extends GenericEntity,
  MetaOptions extends GenericMetaOptions,
> = EntityMetaAdapter<Entity, MetaOptions> & EntityDataAdapter<Entity, MetaOptions>;

export type EntityCollectionCustomActionsCreator<
  Entity extends GenericEntity,
  MetaOptions extends GenericMetaOptions,
> = (
  adapter: EntityCollectionAdapter<Entity, MetaOptions>,
) => ConfigObject<EntityState<Entity, MetaOptions>>;

export interface EntityCollectionStoreOptions<
  Entity extends GenericEntity,
  MetaOptions extends GenericMetaOptions,
> {
  baseSelector?: (root: any) => EntityState<Entity>;
  idSelector?: GenericIdSelector<Entity>;
  customActions?: EntityCollectionCustomActionsCreator<Entity, MetaOptions>;
  references?: (makeRef: MakeRef<Entity>) => Reference<Entity>[];
  initialState?: EntityState<Entity>;
}

export type EntityCollectionStoreCustomActions<
  StoreOptions extends EntityCollectionStoreOptions<any, any>,
> = StoreOptions['customActions'] extends EntityCollectionCustomActionsCreator<any, any>
  ? ReturnType<StoreOptions['customActions']>
  : {};
